import { useCallback, useContext, useMemo } from 'react'

import { getSanityImageUrl } from '../../../lib/image'
import { type FrameBuilderTexture } from '../../../lib/state'
import { FrameBuilderConfigurationContext } from '../../../contexts/configuration-context'
import { FrameBuilderSelectedStateContext } from '../../../contexts/state-context'
import { FrameBuilderStringsContext } from '../../../contexts/strings-context'
import { FrameBuilderValidationContext } from '../../../contexts/validation-context'

import ListContainer from '../../utility/list-container'
import SmallGraphic from '../../utility/small-graphic'

interface ProfileDetailsProps {
  profileId: string
}

const ProfileDetails = ({ profileId }: ProfileDetailsProps) => {
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { lastValidState } = useContext(FrameBuilderValidationContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const configuration = useContext(FrameBuilderConfigurationContext)

  const filteredTextures = useMemo(
    () =>
      configuration.textures.filter((texture) =>
        texture.availableFor?.some(
          (linkedProfile) => linkedProfile.profile._ref === profileId,
        ),
      ),
    [configuration.textures, profileId],
  )

  const selectTexture = useCallback(
    (texture: FrameBuilderTexture) => {
      dispatchSelectedState({
        type: 'frameStyle',
        texture,
      })
    },
    [dispatchSelectedState],
  )

  return (
    <div className="w-full border-t border-gray-300 pt-3 pb-4 px-4">
      <div className="flex flex-col">
        {frameBuilderStrings.frameStyleTextureLabel && (
          <span>{frameBuilderStrings.frameStyleTextureLabel}</span>
        )}

        <ListContainer gapSize="xs" className="mt-2">
          {filteredTextures.map((texture) => {
            const image = texture.icon ?? texture.image
            const imageUrl = getSanityImageUrl(image, {
              width: 42,
              height: 45,
            })

            return (
              <SmallGraphic
                key={texture._id}
                backgroundColor="white"
                backgroundImage={imageUrl}
                selected={selectedState.texture?._id === texture._id}
                error={lastValidState.texture?._id === texture._id}
                onClick={() => selectTexture(texture)}
              />
            )
          })}
        </ListContainer>
      </div>

      {selectedState.texture && (
        <p className="mt-4 text-gray-500 leading-6">
          {selectedState.texture.title}
        </p>
      )}
    </div>
  )
}

export default ProfileDetails
